import React from "react";
import * as Lucide from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import Seo from "@/components/Seo";
import CtaStrip from "@/components/CtaStrip";
import NotFoundPage from "@/pages/NotFoundPage";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SERVICE_PAGES } from "@/lib/servicePages";
import { SERVICES, BUSINESS, COMPLIANCE_LINE_SHORT } from "@/lib/constants";

export default function ServiceDetailPage({ slug }) {
    const page = SERVICE_PAGES[slug];
    if (!page) return <NotFoundPage />;

    const faqLd = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": page.faqs.map((f) => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
    };

    const related = page.related
        .map((r) => SERVICES.find((s) => s.slug === r))
        .filter(Boolean);

    return (
        <div data-testid={`page-service-${slug}`} className="pt-20">
            <Seo
                title={page.titleTag}
                description={page.metaDescription}
                keywords={page.keywords}
                canonicalPath={`/${slug}`}
                jsonLd={faqLd}
            />

            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-20 md:py-28">
                    <div className="max-w-3xl">
                        <div className="overline">{page.overline}</div>
                        <h1 className="mt-4 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.02] text-[#1A362B] font-medium">
                            {page.h1}
                        </h1>
                        <div className="divider-thin my-8" />
                        <p className="text-lg text-[#1C1C1C]/75 leading-relaxed font-light">{page.intro}</p>
                    </div>
                </div>
            </section>

            <section className="bg-background">
                <div className="container-prose py-20">
                    <div className="max-w-3xl space-y-14">
                        {page.sections.map((sec, i) => (
                            <div key={i}>
                                <h2 className="font-serif text-3xl text-[#1A362B] tracking-tight">{sec.heading}</h2>
                                {sec.paragraphs.map((p, j) => (
                                    <p key={j} className="mt-4 text-[#1C1C1C]/80 leading-relaxed">{p}</p>
                                ))}
                                {sec.list && (
                                    <ul className="mt-4 list-disc pl-6 space-y-2 text-[#1C1C1C]/80">
                                        {sec.list.map((item, k) => (
                                            <li key={k} className="leading-relaxed">{item}</li>
                                        ))}
                                    </ul>
                                )}
                                {sec.link && (
                                    <Link to={sec.link.to} className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#1A362B] border-b border-[#C69C6D] pb-1 hover:gap-3 transition-all">
                                        {sec.link.label} <ArrowRight className="w-4 h-4" />
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {page.relatedPosts.length > 0 && (
                <section className="bg-background border-t border-border">
                    <div className="container-prose py-12">
                        <div className="max-w-3xl">
                            <div className="overline mb-6">Further reading</div>
                            <ul className="space-y-3">
                                {page.relatedPosts.map((post) => (
                                    <li key={post.slug}>
                                        <Link
                                            to={`/blog/${post.slug}`}
                                            data-testid={`service-related-post-${post.slug}`}
                                            className="inline-flex items-center gap-3 text-[#1A362B] hover:text-[#C69C6D] transition font-medium"
                                        >
                                            <BookOpen className="w-4 h-4 text-[#C69C6D] shrink-0" /> {post.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>
            )}

            <section className="bg-[#F3F1EA]">
                <div className="container-prose py-20">
                    <div className="max-w-3xl">
                        <div className="overline">Common questions</div>
                        <h2 className="mt-4 font-serif text-4xl tracking-tight text-[#1A362B] font-medium">
                            Frequently asked questions.
                        </h2>
                        <div className="divider-thin my-8" />
                        <Accordion type="single" collapsible data-testid="service-faq" className="w-full">
                            {page.faqs.map((f, i) => (
                                <AccordionItem key={i} value={`faq-${i}`} data-testid={`service-faq-item-${i}`}>
                                    <AccordionTrigger className="font-serif text-lg text-[#1A362B]">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-[#1C1C1C]/75 leading-relaxed">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            <section className="bg-background">
                <div className="container-prose py-16">
                    <div className="max-w-3xl">
                        <div className="overline mb-6">Explore more services</div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {related.map((s) => {
                                const Icon = Lucide[s.icon] || Lucide.CircleDot;
                                return (
                                    <Link
                                        key={s.slug}
                                        to={`/${s.slug}`}
                                        data-testid={`service-related-${s.slug}`}
                                        className="group bg-white border border-border p-6 rounded-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                                    >
                                        <Icon className="w-5 h-5 text-[#1A362B]" />
                                        <div className="mt-3 font-serif text-xl text-[#1A362B]">{s.title}</div>
                                        <div className="mt-2 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-[#C69C6D]">
                                            Learn more <ArrowRight className="w-3.5 h-3.5" />
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                        <p data-testid="service-disclaimer" className="mt-12 text-xs text-[#1C1C1C]/55 leading-relaxed italic border-l-2 border-[#C69C6D] pl-3">
                            Disclaimer: {BUSINESS.disclaimer}
                        </p>
                        <p data-testid="service-credit-disclosure" className="mt-4 text-xs text-[#1C1C1C]/55 leading-relaxed italic border-l-2 border-[#C69C6D] pl-3">
                            {COMPLIANCE_LINE_SHORT}
                        </p>
                    </div>
                </div>
            </section>

            <CtaStrip />
        </div>
    );
}
